import React, { FC, ChangeEvent } from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { connect, FormikProps } from 'formik';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { compose } from 'recompose';
import moment from 'moment';

import { Report, RunReportInput } from 'api';
import { DatePickers, Loaders, PaddedPaper } from 'components';

interface IProps {
  reports: Report[];
  working: boolean;
}

interface IPropsEnhanced extends IProps, WithStyles<typeof styles> {
  formik: FormikProps<RunReportInput>;
}

const MAX_DATE = moment()
  .endOf('day')
  .subtract(1, 'day')
  .format();

const styles = (theme: Theme) =>
  createStyles({
    container: {
      marginBottom: theme.spacing(5),
    },
    title: {
      marginBottom: theme.spacing(3),
    },
    button: {
      width: 150,
      marginBottom: theme.spacing(0.5),
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(connect, withStyles(styles));

const SearchForm: FC<IPropsEnhanced> = ({ classes, formik, reports, working }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, isValid } = formik;
  const { report_name } = values;

  const endAtError = formik.errors.end_at;

  function handleChange(event: ChangeEvent<HTMLSelectElement>) {
    setFieldValue('report_name', event.target.value);
  }

  function renderReportSelector() {
    return (
      <FormControl fullWidth>
        <InputLabel>{t('reports:selectReport')}</InputLabel>
        <Select value={report_name} onChange={handleChange}>
          {reports.map(({ name, description }) => (
            <MenuItem key={name} value={name} title={description}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  function renderGenerateButton() {
    const loader = (
      <div className={classes.button}>
        <Loaders.Standard height={38} />
      </div>
    );
    const generateButton = (
      <Button
        className={classes.button}
        type="submit"
        color="primary"
        variant="contained"
        size="large"
        disabled={!isValid}>
        {t('reports:generate')}
      </Button>
    );

    return working ? loader : generateButton;
  }

  return (
    <PaddedPaper className={classes.container}>
      <Typography variant="h4" color="textPrimary" className={classes.title}>
        {t('reports:generateNew')}
      </Typography>
      <Grid container alignItems="flex-end" justify="space-between" spacing={2}>
        <Grid xs={4} item>
          {renderReportSelector()}
        </Grid>
        <Grid item>
          <DatePickers.Select
            value={values.start_at}
            setValue={time => setFieldValue('start_at', time)}
            maxDate={MAX_DATE}
            label={t('reports:startAt')}
          />
        </Grid>
        <Grid item xs>
          <DatePickers.Select
            value={values.end_at}
            setValue={time => setFieldValue('end_at', time)}
            maxDate={MAX_DATE}
            label={t('reports:endAt')}
          />
        </Grid>
        <Grid item>{renderGenerateButton()}</Grid>
      </Grid>
      {endAtError && <FormHelperText error>{endAtError}</FormHelperText>}
    </PaddedPaper>
  );
};

export default enhancers(SearchForm);
